import type { RefObject } from 'react'
import { useRef } from 'react'
import type { VirtuosoHandle } from 'react-virtuoso'

import type { AnimatedIdsRef } from './chat-animated-item.atom'
import type { ChatItem } from './chat-message.interface'
import { useAutoLoadAtTop } from './use-auto-load-at-top.hook'
import { useChatScrollBehavior } from './use-chat-scroll-behavior.hook'
import { usePrependScrollCompensation } from './use-prepend-scroll-compensation.hook'

export interface UseChatVirtualListReturn {
  virtuosoRef: RefObject<VirtuosoHandle | null>
  animatedIdsRef: AnimatedIdsRef
  showScrollButton: boolean
  firstItemIndex: number
  handleAtTopStateChange: (atTop: boolean) => void
  handleAtBottomStateChange: (atBottom: boolean) => void
  scrollToBottom: () => void
  handleFollowOutput: (isCurrentlyAtBottom: boolean) => 'smooth' | false
}

interface UseChatVirtualListParams {
  itemList: ChatItem[]
  conversationId?: string
  isFollowOutputEnabled: boolean
  isFetchingMoreMessages?: boolean
  hasMoreMessages?: boolean
  onLoadMore?: () => void
}

export const useChatVirtualList = ({
  itemList,
  conversationId,
  isFollowOutputEnabled,
  isFetchingMoreMessages = false,
  hasMoreMessages = false,
  onLoadMore,
}: UseChatVirtualListParams): UseChatVirtualListReturn => {
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const animatedIdsRef = useRef<Set<string>>(new Set())
  const prevConversationIdRef = useRef(conversationId)

  if (prevConversationIdRef.current !== conversationId) {
    animatedIdsRef.current.clear()
    prevConversationIdRef.current = conversationId
  }

  const { showScrollButton, handleAtBottomStateChange, scrollToBottom, handleFollowOutput } =
    useChatScrollBehavior({ virtuosoRef, itemList, isFollowOutputEnabled })

  const firstItem = itemList[0]
  const firstItemIndex = usePrependScrollCompensation({
    itemLength: itemList.length,
    firstItemKey: firstItem?.stableKey ?? firstItem?.id,
    conversationId,
  })

  const { handleAtTopStateChange } = useAutoLoadAtTop({
    hasMoreMessages,
    isFetchingMoreMessages,
    conversationId,
    onLoadMore,
  })

  return {
    virtuosoRef,
    animatedIdsRef,
    showScrollButton,
    firstItemIndex,
    handleAtTopStateChange,
    handleAtBottomStateChange,
    scrollToBottom,
    handleFollowOutput,
  }
}
